import {
  getTranslations,
  type GetTranslationsData,
} from '@cfa/pathway-fe-shared/xp';

import type { QuizLocale, V2QuizEditorState } from './state';
import { oppositeLanguage } from './state';
import { isTrueFalseQuestion } from './v2QuizValidation';

// Temporary client max until the backend enforces field / translated length.
export const MAX_V2_QUIZ_TEXT_LENGTH = 250;

/** Opposite-locale text from translate; translations can exceed primary length. */
export const MAX_V2_QUIZ_TRANSLATED_TEXT_LENGTH = 750;

/** Backoff between attempts; length + 1 is the total attempt count. */
export const TRANSLATE_RETRY_DELAYS_MS = [500, 1500];

const clampTranslatedText = (value: string): string =>
  value.length > MAX_V2_QUIZ_TRANSLATED_TEXT_LENGTH
    ? value.slice(0, MAX_V2_QUIZ_TRANSLATED_TEXT_LENGTH)
    : value;

/**
 * POST /translate, retrying network errors and 5xx. A 4xx will not succeed on
 * retry. Resolves `null` when the request threw on every attempt.
 */
const requestTranslations = async (body: GetTranslationsData['body']) => {
  for (let attempt = 0; ; attempt++) {
    const result = await getTranslations({ body }).catch(() => null);
    const status = result?.response?.status;
    const isRetryable =
      result === null ||
      (result.error !== undefined && (status === undefined || status >= 500));
    const delay = TRANSLATE_RETRY_DELAYS_MS[attempt];
    if (!isRetryable || delay === undefined) {
      return result;
    }
    await new Promise<void>(resolve => {
      setTimeout(resolve, delay);
    });
  }
};

export const translateText = async (
  value: string,
  sourceLanguage: QuizLocale,
): Promise<string | null> => {
  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }

  const targetLanguage = oppositeLanguage(sourceLanguage);
  const result = await requestTranslations({
    sourceLanguage,
    targetLanguage,
    texts: [{ id: 'text', value: trimmed }],
  });

  if (!result || result.error !== undefined) {
    return null;
  }

  const translated = result.data.translations[0]?.translatedText;
  return typeof translated === 'string' && translated.length > 0
    ? clampTranslatedText(translated)
    : null;
};

/** Translate a renamed quiz title into both supported locales (PATHWAY-4043). */
export const buildTranslatedQuizName = async (
  value: string,
  sourceLanguage: QuizLocale,
): Promise<{ en: string; es: string } | null> => {
  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }

  const translated = await translateText(trimmed, sourceLanguage);
  if (!translated) {
    return null;
  }

  return {
    en: sourceLanguage === 'en' ? trimmed : translated,
    es: sourceLanguage === 'es' ? trimmed : translated,
  };
};

/**
 * Re-translate every field the user authored into the other locale.
 * `sourceLanguage` is Settings language (what the editor shows), not necessarily
 * `state.primaryLanguage` (immutable after create).
 * This exists to avoid saving too early and saving stale translations.
 * Full re-translate runs on early leave; `onlyMissing` fills fields whose
 * translation is empty (e.g. after a failed per-field translate).
 */
export interface ApplyAllTranslationsResult {
  ok: boolean;
  state: V2QuizEditorState;
}

const needsTranslation = (
  text: { en?: string; es?: string } | undefined,
  sourceLanguage: QuizLocale,
  onlyMissing: boolean,
) => {
  const source = text?.[sourceLanguage]?.trim() ?? '';
  if (!source) {
    return '';
  }
  if (onlyMissing && text?.[oppositeLanguage(sourceLanguage)]?.trim()) {
    return '';
  }
  return source;
};

/** True when any authored field is missing its translation. */
export const hasUntranslatedText = (
  state: V2QuizEditorState,
  sourceLanguage: QuizLocale,
) =>
  needsTranslation(state.name, sourceLanguage, true) !== '' ||
  state.questions.some(
    question =>
      needsTranslation(question.question, sourceLanguage, true) !== '' ||
      (!isTrueFalseQuestion(question) &&
        question.answers.some(
          answer =>
            needsTranslation(answer.answer, sourceLanguage, true) !== '',
        )),
  );

export const applyAllTranslations = async (
  state: V2QuizEditorState,
  sourceLanguage: QuizLocale = state.primaryLanguage,
  { onlyMissing = false }: { onlyMissing?: boolean } = {},
): Promise<ApplyAllTranslationsResult> => {
  const other = oppositeLanguage(sourceLanguage);
  const texts: { id: string; value: string }[] = [];

  const title = needsTranslation(state.name, sourceLanguage, onlyMissing);
  if (title) {
    texts.push({ id: 'title', value: title });
  }

  state.questions.forEach((question, questionIndex) => {
    const prompt = needsTranslation(
      question.question,
      sourceLanguage,
      onlyMissing,
    );
    if (prompt) {
      texts.push({ id: `q${questionIndex}-prompt`, value: prompt });
    }
    if (isTrueFalseQuestion(question)) {
      return;
    }
    question.answers.forEach((answer, answerIndex) => {
      const primary = needsTranslation(
        answer.answer,
        sourceLanguage,
        onlyMissing,
      );
      if (primary) {
        texts.push({
          id: `q${questionIndex}-a${answerIndex}`,
          value: primary,
        });
      }
    });
  });

  if (texts.length === 0) {
    return { ok: true, state };
  }

  const result = await requestTranslations({
    sourceLanguage,
    targetLanguage: other,
    texts,
  });

  if (!result || result.error !== undefined) {
    return { ok: false, state };
  }

  const byId = new Map(
    result.data.translations.map(translation => [
      translation.id,
      typeof translation.translatedText === 'string'
        ? clampTranslatedText(translation.translatedText)
        : translation.translatedText,
    ]),
  );

  const titleTranslated = byId.get('title');
  const name =
    typeof titleTranslated === 'string' && titleTranslated.length > 0
      ? { ...state.name, [other]: titleTranslated }
      : state.name;

  return {
    ok: true,
    state: {
      ...state,
      name,
      questions: state.questions.map((question, questionIndex) => {
        const promptTranslated = byId.get(`q${questionIndex}-prompt`);
        const nextQuestion =
          typeof promptTranslated === 'string' && promptTranslated.length > 0
            ? { ...question.question, [other]: promptTranslated }
            : question.question;

        if (isTrueFalseQuestion(question)) {
          return { ...question, question: nextQuestion };
        }

        return {
          ...question,
          question: nextQuestion,
          answers: question.answers.map((answer, answerIndex) => {
            const translated = byId.get(`q${questionIndex}-a${answerIndex}`);
            if (typeof translated !== 'string' || translated.length === 0) {
              return answer;
            }
            return {
              ...answer,
              answer: { ...answer.answer, [other]: translated },
            };
          }),
        };
      }),
    },
  };
};
